import React, { MouseEventHandler, useEffect } from "react";
import { ButtonProps, Theme } from "@literati/public-ui-shared-react";
import { FormikChildrenRenderProps } from "components/Step/StepLayout";
import MountTransition from "utils/animations/MountTransition";

import AuthFormSectionCreateAccount from "components/authenticate/AuthFormSectionCreateAccount";
import AuthFormSectionDummyAccount from "components/authenticate/AuthFormSectionDummyAccount";
import AuthFormSectionLogin from "components/authenticate/AuthFormSectionLogin";
import {
  AuthValues,
  FormAction,
  SocialLoginSuccess,
} from "components/authenticate/authTypes";

export type SubmitStateChangeEvent = {
  disableSubmit: boolean;
  isSubmitting: boolean;
};
export type SubmitStateChangeEventHandler = (
  event: SubmitStateChangeEvent
) => void;

export interface AuthFormSectionsProps
  extends FormikChildrenRenderProps<AuthValues> {
  formAction: FormAction;
  formId?: string;
  onFormToggleClick?: MouseEventHandler<HTMLButtonElement>;
  onPhoneNumberChange?: (phoneNumber: string) => void;
  onSubmitStateChange?: SubmitStateChangeEventHandler;
  onSocialLoginSuccess?: SocialLoginSuccess;
  showSocialLogin?: boolean;
  submitButtonProps?: ButtonProps;
  useFadeTransition?: boolean;
  withTerms?: boolean;
}

export const AuthFormSections = ({
  formAction,
  onPhoneNumberChange,
  onSubmitStateChange,
  useFadeTransition = false,
  ...passThroughProps
}: AuthFormSectionsProps) => {
  // Allows parents to control submit button state while using Formik render prop
  const { dirty, isSubmitting, isValid, setErrors } = passThroughProps;

  useEffect(() => {
    const disableSubmit = !dirty || (dirty && !isValid);
    onSubmitStateChange?.({ disableSubmit, isSubmitting });
  }, [dirty, isSubmitting, isValid, onSubmitStateChange]);

  useEffect(() => {
    setErrors({});
  }, [formAction, setErrors]);

  return (
    <Theme>
      <>
        {formAction === "createAccount" ? (
          useFadeTransition ? (
            <MountTransition
              preset="crossFade"
              show={formAction === "createAccount"}
            >
              <AuthFormSectionCreateAccount
                {...passThroughProps}
                onPhoneNumberChange={onPhoneNumberChange}
              />
            </MountTransition>
          ) : (
            <AuthFormSectionCreateAccount
              {...passThroughProps}
              onPhoneNumberChange={onPhoneNumberChange}
            />
          )
        ) : null}
        {formAction === "createDummyAccount" ? (
          useFadeTransition ? (
            <MountTransition
              preset="crossFade"
              show={formAction === "createDummyAccount"}
            >
              <AuthFormSectionDummyAccount {...passThroughProps} />
            </MountTransition>
          ) : (
            <AuthFormSectionDummyAccount {...passThroughProps} />
          )
        ) : null}
        {formAction === "login" ? (
          useFadeTransition ? (
            <MountTransition preset="crossFade" show={formAction === "login"}>
              <AuthFormSectionLogin {...passThroughProps} />
            </MountTransition>
          ) : (
            <AuthFormSectionLogin {...passThroughProps} />
          )
        ) : null}
      </>
    </Theme>
  );
};

export default AuthFormSections;
